import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getGlobalLeaderboard } from '../services/api';
import { Trophy, Medal, ArrowLeft, Crown, Sparkles, TrendingUp, Award, Star, ChevronUp } from 'lucide-react';
import { DotLoader } from 'react-spinners';
import { useAuth } from '../context/AuthContext';
import defaultAvatar from '../assets/default-avatar.jpg';


const getMedalIcon = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return null;
  };
function GlobalLeaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const userCardRef = useRef(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const data = await getGlobalLeaderboard();
      setLeaderboard(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const scrollToUser = () => {
    if (userCardRef.current) {
      userCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  // Trouver la position de l'utilisateur
  const userPosition = leaderboard.find(entry => entry.user._id === user?._id);
  const userInTop3 = userPosition && userPosition.position <= 3;

  

  const getPositionStyle = (position) => {
    if (position === 1) {
      return {
        container: 'bg-gradient-to-br from-yellow-900/40 via-yellow-800/30 to-amber-900/40 border-2 border-yellow-500/60 shadow-2xl shadow-yellow-500/30',
        glow: 'from-yellow-500/20 to-amber-500/20',
        text: 'text-yellow-100',
        badge: 'bg-gradient-to-r from-yellow-500 to-amber-500',
        ring: 'ring-4 ring-yellow-500/30'
      };
    }
    if (position === 2) {
      return {
        container: 'bg-gradient-to-br from-indigo-900/40 via-indigo-800/30 to-indigo-900/40 border-2 border-indigo-500/60 shadow-xl shadow-indigo-500/20',
        glow: 'from-indigo-500/20 to-indigo-600/20',
        text: 'text-indigo-100',
        badge: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
        ring: 'ring-4 ring-indigo-500/30'
      };
    }
    if (position === 3) {
      return {
        container: 'bg-gradient-to-br from-slate-700/40 via-slate-600/30 to-slate-700/40 border-2 border-slate-500/60 shadow-xl shadow-slate-500/20',
        glow: 'from-slate-500/20 to-slate-600/20',
        text: 'text-slate-100',
        badge: 'bg-gradient-to-r from-slate-500 to-slate-600',
        ring: 'ring-4 ring-slate-500/30'
      };
    }
    return {
      container: 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border-2 border-slate-700/50 hover:border-slate-600',
      glow: 'from-indigo-600/10 to-indigo-500/10',
      text: 'text-white',
      badge: 'bg-slate-700',
      ring: ''
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  const top3 = leaderboard.slice(0, 3);
  const others = leaderboard.slice(3);

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-slate-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <Navbar />

      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pb-24 sm:pb-28">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 transition text-sm sm:text-base group"
        >
          <ArrowLeft size={18} className="mr-2 sm:w-5 sm:h-5 group-hover:-translate-x-1 transition-transform" />
          Retour
        </button>

        {/* Header */}
        <div className="relative mb-8 sm:mb-12">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/10 via-indigo-500/10 to-indigo-600/10 blur-3xl"></div>

          <div className="relative bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8 overflow-hidden">
            {/* Animated border */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent animate-pulse"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600"></div>

            <div className="relative z-10 text-center">
              {/* Icon with animation */}
              <div className="inline-flex items-center justify-center mb-4 sm:mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-yellow-500/30 blur-2xl rounded-full animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 p-4 sm:p-6 rounded-3xl border border-yellow-500/40">
                    <Trophy size={36} className="text-yellow-500 sm:w-12 sm:h-12 animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 sm:mb-4 bg-gradient-to-r from-indigo-400 to-indigo-500 bg-clip-text text-transparent animate-fade-in">
                Classement Mondial
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-gray-400 flex items-center justify-center gap-2">
                <Sparkles size={16} className="text-yellow-500 sm:w-5 sm:h-5 animate-pulse" />
                Les meilleurs athlètes de la plateforme
                <Sparkles size={16} className="text-yellow-500 sm:w-5 sm:h-5 animate-pulse" />
              </p>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600"></div>
          </div>
        </div>

        {/* Top 3 Podium */}
        {top3.length > 0 && (
          <div className="mb-8 sm:mb-12">
            <div className="flex flex-col md:flex-row items-end justify-center gap-3 md:gap-6 mb-8">
              {/* 2nd Place */}
              {top3[1] && (
                <div
                  ref={top3[1].user._id === user?._id ? userCardRef : null}
                  className="w-full md:w-64 order-1 md:order-1 animate-slide-up"
                  style={{ animationDelay: '0.2s' }}
                >
                  <PodiumCard participant={top3[1]} position={2} />
                </div>
              )}

              {/* 1st Place */}
              {top3[0] && (
                <div
                  ref={top3[0].user._id === user?._id ? userCardRef : null}
                  className="w-full md:w-80 order-0 md:order-2 animate-slide-up"
                  style={{ animationDelay: '0.1s' }}
                >
                  <PodiumCard participant={top3[0]} position={1} isFirst={true} />
                </div>
              )}

              {/* 3rd Place */}
              {top3[2] && (
                <div
                  ref={top3[2].user._id === user?._id ? userCardRef : null}
                  className="w-full md:w-64 order-2 md:order-3 animate-slide-up"
                  style={{ animationDelay: '0.3s' }}
                >
                  <PodiumCard participant={top3[2]} position={3} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rest of leaderboard */}
        {others.length > 0 && (
          <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-indigo-500 sm:w-6 sm:h-6" />
              Classement Complet
            </h2>

            <div className="space-y-2 sm:space-y-3 max-w-4xl mx-auto">
              {others.map((participant, index) => {
                const actualPosition = index + 4;
                const isCurrentUser = participant.user._id === user?._id;
                const styles = getPositionStyle(actualPosition);

                return (
                  <div
                    key={participant.user._id}
                    ref={participant.user._id === user?._id ? userCardRef : null}
                    onClick={() => navigate(`/user/${participant.user._id}`)}
                    className={`group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer ${styles.container} animate-fade-in`}
                    style={{ animationDelay: `${0.4 + index * 0.05}s` }}
                  >
                    {/* Glow effect on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${styles.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>

                    {/* Current user indicator */}
                    {isCurrentUser && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
                    )}

                    <div className="relative z-10 p-3 sm:p-4">
                      <div className="flex items-center gap-3 sm:gap-4">
                        {/* Position */}
                        <div className="flex-shrink-0 w-8 sm:w-10 text-center">
                          <span className="text-lg sm:text-xl font-black text-gray-400">
                            #{actualPosition}
                          </span>
                        </div>

                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          <img
                            src={participant.user.photoProfil || defaultAvatar}
                            alt={`${participant.user.prenom} ${participant.user.nom}`}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-slate-600 group-hover:border-indigo-500 transition-all duration-300"
                          />
                          {isCurrentUser && (
                            <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 bg-indigo-600 text-white text-[8px] sm:text-[10px] font-bold rounded-full border-2 border-slate-900">
                              VOUS
                            </div>
                          )}
                        </div>

                        {/* Name & City */}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm sm:text-base text-white truncate">
                            {participant.user.prenom} {participant.user.nom}
                          </p>
                          {participant.user.ville && (
                            <p className="text-xs sm:text-sm text-gray-400 truncate">
                              📍 {participant.user.ville}
                            </p>
                          )}
                        </div>

                        {/* Points */}
                        <div className="flex-shrink-0 text-right">
                          <p className="text-lg sm:text-xl font-black text-white">
                            {participant.points}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-400">points</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {leaderboard.length === 0 && (
          <div className="text-center py-12 sm:py-16">
            <Trophy size={48} className="mx-auto text-slate-700 mb-4 sm:w-16 sm:h-16" />
            <p className="text-base sm:text-lg text-gray-400">Aucun classement disponible pour le moment</p>
          </div>
        )}
      </main>

      {/* Footer sticky avec position utilisateur */}
      {userPosition && (
        <div className="fixed bottom-0 left-0 right-0 z-50 pb-safe">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-3 sm:pb-4">
            <button
              onClick={scrollToUser}
              className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 border-2 border-indigo-500/50 rounded-xl shadow-2xl shadow-indigo-500/30 p-3 sm:p-4 transition-all active:scale-[0.98] group relative overflow-hidden"
            >
              {/* Effet de brillance */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:via-white/20 transition-all"></div>

              <div className="relative flex items-center justify-between">
                {/* Info gauche */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Avatar */}
                  <img
                    src={user?.photoProfil || defaultAvatar}
                    alt="Vous"
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white/30 shadow-lg object-cover"
                  />

                  {/* Position et nom */}
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      {userPosition.position <= 3 && (
                        <span className="text-lg sm:text-xl">{getMedalIcon(userPosition.position)}</span>
                      )}
                      <p className="text-base sm:text-lg font-black text-white">
                        #{userPosition.position}
                      </p>
                    </div>
                    <p className="text-xs sm:text-sm text-indigo-200 font-medium">Votre position</p>
                  </div>
                </div>

                {/* Points et action */}
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Points */}
                  <div className="text-right">
                    <p className="text-lg sm:text-xl font-black text-yellow-400">
                      {userPosition.points}
                    </p>
                    <p className="text-xs sm:text-sm text-indigo-200">points</p>
                  </div>

                  {/* Icône scroll */}
                  <div className="bg-white/10 group-hover:bg-white/20 p-2 sm:p-2.5 rounded-lg transition-colors">
                    <ChevronUp size={18} className="text-white sm:w-5 sm:h-5 group-hover:animate-bounce" />
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }

        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}

// Podium Card Component
function PodiumCard({ participant, position, isFirst = false }) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isCurrentUser = participant.user._id === currentUser?._id;
  const styles = getPositionStyle(position);

  return (
    <div className="relative">
      {/* Confetti effect for first place */}
      {isFirst && (
        <>
          <div className="absolute -top-2 md:-top-4 -left-2 md:-left-4 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0s' }}></div>
          <div className="absolute -top-3 md:-top-6 left-1/4 w-2 h-2 bg-yellow-500 rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
          <div className="absolute -top-2 md:-top-4 -right-2 md:-right-4 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
        </>
      )}

      <div
        onClick={() => navigate(`/user/${participant.user._id}`)}
        className={`relative overflow-hidden rounded-2xl transition-all duration-500 hover:scale-105 cursor-pointer ${styles.container} ${styles.ring}`}
      >
        {/* Animated glow */}
        <div className={`absolute inset-0 bg-gradient-to-r ${styles.glow} animate-pulse`}></div>

        {/* Crown for first place */}
        {isFirst && (
          <div className="absolute -top-4 md:-top-8 left-1/2 -translate-x-1/2 z-20">
            <Crown size={24} className="text-yellow-500 animate-bounce md:w-8 md:h-8" />
          </div>
        )}

        {/* Medal badge */}
        <div className="absolute top-2 md:top-4 right-2 md:right-4 z-20">
          <div className={`${styles.badge} px-2 py-1 md:px-3 md:py-1.5 rounded-full shadow-lg`}>
            <span className="text-lg md:text-2xl">{getMedalIcon(position)}</span>
          </div>
        </div>

        <div className="relative z-10 p-3 md:p-6 text-center">
          {/* Avatar */}
          <div className="relative inline-block mb-2 md:mb-4">
            <div className={`absolute inset-0 ${styles.badge} blur-xl rounded-full opacity-50 animate-pulse`}></div>
            <img
              src={participant.user.photoProfil || defaultAvatar}
              alt={`${participant.user.prenom} ${participant.user.nom}`}
              className={`relative w-16 h-16 md:w-24 md:h-24 lg:w-28 lg:h-28 ${isFirst ? 'lg:w-32 lg:h-32' : ''} rounded-full object-cover border-4 ${
                isFirst ? 'border-yellow-500' : position === 2 ? 'border-indigo-500' : 'border-slate-500'
              } shadow-2xl`}
            />
            {isCurrentUser && (
              <div className="absolute -bottom-1 md:-bottom-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 md:px-2 md:py-1 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full border-2 border-slate-900 shadow-lg">
                VOUS
              </div>
            )}
          </div>

          {/* Name */}
          <h3 className={`text-base md:text-lg lg:text-xl ${isFirst ? 'lg:text-2xl' : ''} font-black ${styles.text} mb-1`}>
            {participant.user.prenom} {participant.user.nom}
          </h3>

          {participant.user.ville && (
            <p className="text-[10px] md:text-xs lg:text-sm text-gray-400 mb-2 md:mb-4">
              📍 {participant.user.ville}
            </p>
          )}

          {/* Points */}
          <div className="mt-2 md:mt-4">
            <div className={`inline-flex items-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 ${styles.badge} rounded-full shadow-lg`}>
              <Star size={14} className="text-white md:w-4 md:h-4" />
              <span className="text-base md:text-xl lg:text-2xl font-black text-white">
                {participant.points}
              </span>
              <span className="text-xs md:text-sm text-white/80">pts</span>
            </div>
          </div>

          {/* Position indicator */}
          <div className="mt-2 md:mt-4">
            <div className="text-4xl md:text-6xl lg:text-7xl font-black opacity-10">
              #{position}
            </div>
          </div>
        </div>

        {/* Bottom border */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${styles.glow}`}></div>
      </div>
    </div>
  );
}

function getPositionStyle(position) {
  if (position === 1) {
    return {
      container: 'bg-gradient-to-br from-yellow-900/40 via-yellow-800/30 to-amber-900/40 border-2 border-yellow-500/60 shadow-2xl shadow-yellow-500/30',
      glow: 'from-yellow-500/20 to-amber-500/20',
      text: 'text-yellow-100',
      badge: 'bg-gradient-to-r from-yellow-500 to-amber-500',
      ring: 'ring-4 ring-yellow-500/30'
    };
  }
  if (position === 2) {
    return {
      container: 'bg-gradient-to-br from-indigo-900/40 via-indigo-800/30 to-indigo-900/40 border-2 border-indigo-500/60 shadow-xl shadow-indigo-500/20',
      glow: 'from-indigo-500/20 to-indigo-600/20',
      text: 'text-indigo-100',
      badge: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      ring: 'ring-4 ring-indigo-500/30'
    };
  }
  if (position === 3) {
    return {
      container: 'bg-gradient-to-br from-slate-700/40 via-slate-600/30 to-slate-700/40 border-2 border-slate-500/60 shadow-xl shadow-slate-500/20',
      glow: 'from-slate-500/20 to-slate-600/20',
      text: 'text-slate-100',
      badge: 'bg-gradient-to-r from-slate-500 to-slate-600',
      ring: 'ring-4 ring-slate-500/30'
    };
  }
  return {
    container: 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border-2 border-slate-700/50 hover:border-slate-600',
    glow: 'from-indigo-600/10 to-indigo-500/10',
    text: 'text-white',
    badge: 'bg-slate-700',
    ring: ''
  };
}

export default GlobalLeaderboard;
